import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { BroadcastNotifications } from "./components/BroadcastNotifications";
import { PendingApprovalsBadge } from "./components/PendingApprovalsBadge";
import { SupportRequestNotifications } from "./components/SupportRequestNotifications";
import { TaskAssignmentNotifications } from "./components/TaskAssignmentNotifications";
import { GlobalTasksPanel } from "./components/GlobalTasksPanel";
import EmailSyncStatus from "./components/EmailSyncStatus";
import ErrorBoundary from "./components/ErrorBoundary";
import UtilitiesPage from "./pages/UtilitiesPage";
import LandingPage from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EmergencyResetPage from "./pages/EmergencyResetPage";
import OneTimeSetupPage from "./pages/OneTimeSetupPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ChatPage } from "./pages/ChatPage";
import { AdminPage } from "./pages/AdminPage";
import PukManagementPage from "./pages/PukManagementPage";
import { TrackingHistoryPage } from "./pages/TrackingHistoryPage";
import { LCLTrackingPage } from "./pages/LCLTrackingPage";
import { WatchlistPage } from "./pages/WatchlistPage";
import PortTrackingPage from "./pages/PortTrackingPage";
import CO2CalculatorPage from "./pages/CO2CalculatorPage";
import CO2DashboardPage from "./pages/CO2DashboardPage";
import OrderBookPage from "./pages/OrderBookPage";
import RouteCalculatorPage from "./pages/RouteCalculatorPage";
import TransportOrdersPage from "./pages/TransportOrdersPage";
import TruckingBlacklistPage from "./pages/TruckingBlacklistPage";
import DriverTrackingPage from "./pages/DriverTrackingPage";
import PositionFoldersPage from "./pages/PositionFoldersPage";
import QuotesPage from "./pages/QuotesPage";
import MyQuotesPage from "./pages/MyQuotesPage";
import PricingModulePage from "./pages/PricingModulePage";
import PartnerCampaignsPage from "./pages/PartnerCampaignsPage";
import OffersDashboardPage from "./pages/OffersDashboardPage";
import RateCardsPage from "./pages/RateCardsPage";
import TrackingModulePage from "./pages/TrackingModulePage";
import OfferResponsePage from "./pages/OfferResponsePage";
import OfferFeedbackPage from "./pages/OfferFeedbackPage";
import OfferSurveyPage from "./pages/OfferSurveyPage";
import OfferFollowupDashboard from "./pages/OfferFollowupDashboard";
import CustomerReactivationPage from "./pages/CustomerReactivationPage";
import WhatsAppTicketsPage from "./pages/WhatsAppTicketsPage";
import WhatsAppUsersPage from "./pages/WhatsAppUsersPage";
import CRMDocumentsPage from "./pages/CRMDocumentsPage";
import EmailRepositoryPage from "./pages/EmailRepositoryPage";
import OutboundEmailRepositoryPage from "./pages/OutboundEmailRepositoryPage";
import GuidePage from "./pages/GuidePage";
import UserGuidesPage from "./pages/UserGuidesPage";
import UnifiedGuidePage from "./pages/UnifiedGuidePage";
import NewsPage from "./pages/NewsPage";
import SmsManagementPage from "./pages/SmsManagementPage";
import MyCompanyContactsPage from "./pages/MyCompanyContactsPage";
import MyFinancePage from "./pages/MyFinancePage";
import MobileAppPage from "./pages/MobileAppPage";
import DriverLocationPage from "./pages/DriverLocationPage";
import DriverPodUploadPage from "./pages/DriverPodUploadPage";
import PublicTrackingPage from "./pages/PublicTrackingPage";
import CarrierRegistrationPage from "./pages/CarrierRegistrationPage";
import CarrierApprovalsPage from "./pages/CarrierApprovalsPage";
import CustomerApprovalsPage from "./pages/CustomerApprovalsPage";
import PartnerPortalPage from "./pages/PartnerPortalPage";
import PartnerApprovalsPage from "./pages/PartnerApprovalsPage";
import CarrierSetPasswordPage from "./pages/CarrierSetPasswordPage";
import CustomerRegistrationPage from "./pages/CustomerRegistrationPage";
import BLTrackingPage from "./pages/BLTrackingPage";
import DriverContinuousTrackingPage from "./pages/DriverContinuousTrackingPage";
import CRMPage from "./pages/CRM/CRMPage";
import CRMCampaignsPage from "./pages/CRM/CRMCampaignsPage";
import PublicKYCFormPage from "./pages/public/PublicKYCFormPage";
import PublicVGMPage from "./pages/PublicVGMPage";
import PublicFiscalPage from "./pages/PublicFiscalPage";
import CustomsFormFillPage from "./pages/CustomsFormFillPage";
import CustomsPackagePage from "./pages/CustomsPackagePage";
import BillOfLadingPage from "./pages/BillOfLading/BillOfLadingPage";
import TrinetPage from "./pages/TrinetPage";
import LoadCalculatorPage from "./pages/LoadCalculatorPage";
import KYCFormPage from "./pages/KYCFormPage";
import BLInstructionsPage from "./pages/BLInstructionsPage";
import CustomerPortalPage from "./pages/CustomerPortalPage";
import CustomerQuotesPage from "./pages/CustomerQuotesPage";
import CustomerFeedbackPage from "./pages/CustomerFeedbackPage";
import ImportExportPage from "./pages/ImportExportPage";
import FinancePage from "./pages/FinancePage";
import StaffManagementPage from "./pages/StaffManagementPage";
import UserManagementPage from "./pages/UserManagementPage";
import LeaveCalendarPage from "./pages/LeaveCalendarPage";
import ContactDirectoryPage from "./pages/ContactDirectoryPage";
import StaffMobilePage from "./pages/StaffMobilePage";
import CustomerGroupsPage from "./pages/CustomerGroupsPage";
import InspectionViewPage from "./pages/InspectionViewPage";
import TenderBidPage from "./pages/TenderBidPage";
import SpotBidPage from "./pages/SpotBidPage";
import TruckingPortalPage from "./pages/TruckingPortalPage";
import NewsletterPage from "./pages/NewsletterPage";
import UnsubscribeDashboardPage from "./pages/UnsubscribeDashboardPage";
import EmailPreferencesPage from "./pages/EmailPreferencesPage";
import LLMCostControlPage from "./pages/LLMCostControlPage";
import HRBotPage from "./pages/HRBotPage";
import CompliancePage from "./pages/CompliancePage";
import PAOperationsPage from "./pages/PAOperationsPage";
import OutlookAddinPage from "./pages/OutlookAddinPage";
import OutlookAddinSalesPage from "./pages/OutlookAddinSalesPage";
import OutlookAddinFinancePage from "./pages/FinanceAddin/FinanceAddinPage";
import OutlookDownloadPage from "./pages/OutlookDownloadPage";
import SalesLeadsPage from "./pages/SalesLeadsPage";
import SatisfactionSurveyPage from "./pages/SatisfactionSurveyPage";
import NPSSurveyPage from "./pages/NPSSurveyPage";
import NPSPage from "./pages/NPSPage";
import SatisfactionReviewsPage from "./pages/SatisfactionReviewsPage";
import HRPerformancePage from "./pages/HRPerformancePage";
import HRPerformanceAutoPage from "./pages/HRPerformanceAutoPage";
import MyFeedbackPage from "./pages/MyFeedbackPage";
import RateFinderPage from "./pages/RateFinderPage";
import CmaCgmPage from "./pages/CmaCgmPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";
import PricingPage from "./pages/PricingPage";
import VendorAPPage from "./pages/VendorAPPage";
import WMSPage from "./pages/WMSPage";
import WMSLandingPage from "./pages/WMSLandingPage";
import WMSCustomerPortalPage from "./pages/WMSCustomerPortalPage";
import IzvrsbePage from "./pages/IzvrsbePage";
import BankStatusPage from "./pages/BankStatusPage";
import LoansPage from "./pages/LoansPage";
import ARAPPage from "./pages/ARAPPage";
import CashFlowDashboardPage from "./pages/CashFlowDashboardPage";
import GlobalSearchPage from "./pages/GlobalSearchPage";
import PaymentApprovalPage from "./pages/PaymentApprovalPage";
import CashFlowPage from "./pages/CashFlowPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SubscriptionCancelPage from "./pages/SubscriptionCancelPage";
import SupportQueuePage from "./pages/SupportQueuePage";
import LinkedInNewsletterPage from "./pages/LinkedInNewsletterPage";
import VoiceNotesPage from "./pages/VoiceNotesPage";
import APIsPage from "./pages/APIsPage";
import PublicAPIDocsPage from "./pages/PublicAPIDocsPage";
import MetransTrackingPage from "./pages/MetransTrackingPage";
import HealthDashboardPage from "./pages/HealthDashboardPage";
import SystemMonitorPage from "./pages/SystemMonitorPage";
import PartnerDirectoryPage from "./pages/PartnerDirectoryPage";
import CustomerAdvisoryPage from "./pages/CustomerAdvisoryPage";
import CRMDataRefreshPage from "./pages/CRMDataRefreshPage";
import CRMRefreshUpdateFormPage from "./pages/CRMRefreshUpdateFormPage";
import OfferArchivePage from "./pages/OfferArchivePage";
import TaskTemplatesPage from "./pages/TaskTemplatesPage";
import EmailArchivePage from "./pages/EmailArchivePage";
import ShareholderReportsPage from "./pages/ShareholderReportsPage";
import HrUpdatePage from "./pages/HrUpdatePage";
import DeploymentLogPage from "./pages/DeploymentLogPage";
import WeeklyReportsPage from "./pages/WeeklyReportsPage";
import WeeklyTrendsPage from "./pages/WeeklyTrendsPage";
import EmailRulesPage from "./pages/EmailRulesPage";
import OperationsMonitorPage from "./pages/OperationsMonitorPage";
import PublicPayPage from "./pages/PublicPayPage";
import StaffPaymentsPage from "./pages/StaffPaymentsPage";
import StickyNotesSidebar from "./components/common/StickyNotesSidebar";
import MobileViewWrapper from "./components/MobileViewWrapper";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { NewsPopup } from "./components/NewsPopup";
import "./App.css";

// Routes that should NOT show global widgets (external portals, public pages)
const STANDALONE_ROUTES = [
  '/trucking-portal',
  '/outlook-addin',
  '/outlook-addin-sales',
  '/outlook-addin-finance',
  '/outlook-download',
  '/track/',
  '/track-continuous/',
  '/pod/',
  '/tracking/',
  '/offer-response/',
  '/pay/',
  '/offer-feedback/',
  '/offer-survey/',
  '/kyc/',
  '/bl-instructions/',
  '/inspection/',
  '/tender-bid/',
  '/spot-bid/',
  '/satisfaction-survey/',
  '/approve-payment/',
  '/hr-update/',
  '/email-preferences',
  '/customs-form/',
  '/customs-pack/',
  '/wms',
  '/mycargocfs',
  '/utilities',
];

// Component that conditionally renders global widgets based on current route
const GlobalWidgets = () => {
  const location = useLocation();
  
  // Check if current path matches any standalone route
  const isStandalonePage = STANDALONE_ROUTES.some(route => 
    location.pathname === route || location.pathname.startsWith(route)
  );
  
  // Also hide on landing, login, register pages
  const isPublicPage = ['/', '/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname);
  
  if (isStandalonePage || isPublicPage) {
    return null;
  }
  
  return (
    <>
      <NewsPopup />
      <FeedbackWidget />
      <StickyNotesSidebar />
    </>
  );
};

// Staff Tasks Panel - shows at bottom of specific staff module pages only
// Modules: Finance, HR, Operations, Sales & Quotes, Tools, Core windows
// Hidden from: Customer Portal, public pages, standalone portals
const STAFF_PANEL_ALLOWED_ROUTES = [
  // Core / Dashboard
  '/dashboard',
  '/search',
  '/admin',
  '/user-management',
  '/chat',
  '/voice-notes',
  '/guides',
  '/news',
  '/newsletter',
  '/linkedin-newsletter',
  '/hrbot',
  '/compliance',
  '/pa-operations',
  '/directory',
  '/import-export',
  
  // Finance Module
  '/finance',
  '/vendor-ap',
  '/izvrsbe',
  '/bank-status',
  '/loans',
  '/ar-ap',
  '/cashflow-dashboard',
  '/cashflow',
  '/shareholder-reports',
  
  // HR Module
  '/staff',
  '/leave-calendar',
  '/hr-performance',
  '/hr-performance-manual',
  '/my-feedback',
  '/satisfaction-reviews',
  
  // Operations Module — /position-folders has its OWN inline panel (the reference design).
  // All other ops pages now render the GlobalTasksPanel for visual parity.
  '/transport-orders',
  '/order-book',
  '/trucking-blacklist',
  '/bl',
  '/trinet',
  '/load-calculator',
  '/lcl',
  '/watchlist',
  '/port-tracking',
  '/co2-calculator',
  '/route-calculator',
  '/email-repository',
  '/sms-management',
  
  // Sales & Quotes Module
  '/quotes',
  '/offers',
  '/offers/pricing',
  '/offers/rate-cards',
  '/offers/followups',
  '/rate-finder',
  '/crm',
  '/crm/campaigns',
  '/crm-documents',
  '/sales-leads',
  '/customer-groups',
  
  // Tools
  '/tracking',
  '/tracking/lcl',
  '/tracking/port',
  '/tracking/metrans',
  '/tracking/watchlist',
  '/tracking/history',
  '/guide',
  '/integrations',
  '/health',
  '/partner-directory',
];

const StaffTasksPanel = () => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Check if we have a token (user is logged in)
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  // Only show on explicitly allowed staff routes
  const isAllowedRoute = STAFF_PANEL_ALLOWED_ROUTES.some(route => {
    // Exact match or starts with the route (for nested routes)
    return pathname === route || pathname.startsWith(route + '/');
  });
  
  if (!isAllowedRoute) return null;
  
  return <GlobalTasksPanel />;
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <MobileViewWrapper>
          <Routes>
          {/* ========== STANDALONE EXTERNAL PORTALS (No navbar, no widgets) ========== */}
          {/* Trucking Portal - clean standalone interface for external trucking companies */}
          <Route
            path="/trucking-portal"
            element={<TruckingPortalPage />}
          />
          <Route path="/carrier-set-password/:token" element={<CarrierSetPasswordPage />} />
          <Route path="/customer-registration" element={<CustomerRegistrationPage />} />
          
          {/* Outlook Add-in - standalone task pane interface */}
          <Route
            path="/outlook-addin"
            element={<OutlookAddinPage />}
          />
          
          {/* Outlook Sales Add-in - sales-focused task pane interface */}
          <Route
            path="/outlook-addin-sales"
            element={<OutlookAddinSalesPage />}
          />
          
          {/* Outlook Finance Add-in - finance/AP task pane interface */}
          <Route
            path="/outlook-addin-finance"
            element={<OutlookAddinFinancePage />}
          />
          
          {/* Outlook Download Page - manifest download and instructions */}
          <Route
            path="/outlook-download"
            element={<OutlookDownloadPage />}
          />
          <Route
            path="/outlook-download/:pluginType"
            element={<OutlookDownloadPage />}
          />
          
          {/* ========== PUBLIC PAGES (No auth, no widgets) ========== */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/emergency-reset" element={<EmergencyResetPage />} />
          <Route path="/setup-2026" element={<OneTimeSetupPage />} />
          {/* Public API Documentation - external partners can view */}
          <Route path="/docs/lcl-status" element={<PublicAPIDocsPage />} />
          {/* Public driver pages - no auth required */}
          <Route path="/track/:orderId/:token" element={<DriverLocationPage />} />
          <Route path="/track-continuous/:orderId/:token" element={<DriverContinuousTrackingPage />} />
          <Route path="/pod/:orderId/:token" element={<DriverPodUploadPage />} />
          {/* Public customer tracking page - no auth required */}
          <Route path="/tracking/:trackingId/:token" element={<PublicTrackingPage />} />
          {/* Public BL tracking page - no auth required */}
          <Route path="/bl-tracking" element={<BLTrackingPage />} />
          {/* Public offer response pages - no auth required */}
          <Route path="/offer-response/:token" element={<OfferResponsePage />} />
          <Route path="/pay/:token" element={<PublicPayPage />} />
          <Route path="/offer-feedback/:token" element={<OfferFeedbackPage />} />
          <Route path="/offer-survey/:token" element={<OfferSurveyPage />} />
          {/* Public KYC form - no auth required */}
          <Route path="/kyc/:token" element={<KYCFormPage />} />
          {/* Public KYC Campaign form - no auth required */}
          <Route path="/public/kyc/:token" element={<PublicKYCFormPage />} />
          <Route path="/vgm/:token" element={<PublicVGMPage />} />
          <Route path="/fiscal/:token" element={<PublicFiscalPage />} />
          <Route path="/customs-form/:slug" element={<CustomsFormFillPage />} />
          <Route path="/customs-pack/:token" element={<CustomsPackagePage />} />
          {/* Terms and Conditions - public page */}
          <Route path="/terms" element={<TermsAndConditionsPage />} />
          {/* Pricing & Plans - public page */}
          <Route path="/pricing" element={<PricingPage />} />
          {/* Subscription success/cancel pages - public */}
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
          {/* Public BL Instructions form - no auth required */}
          <Route path="/bl-instructions/:pfId/:token" element={<BLInstructionsPage />} />
          {/* Public Inspection View - no auth required */}
          <Route path="/inspection/:inspectionId/:token" element={<InspectionViewPage />} />
          {/* Public Tender Bid Page - no auth required */}
          <Route path="/tender-bid/:tenderId/:token" element={<TenderBidPage />} />
          <Route path="/spot-bid/:spotId/:token" element={<SpotBidPage />} />
          {/* Public Satisfaction Survey - no auth required */}
          <Route path="/satisfaction-survey/:token" element={<SatisfactionSurveyPage />} />
          {/* Public NPS Survey - no auth required */}
          <Route path="/nps-survey/:token" element={<NPSSurveyPage />} />
          {/* Public Payment Approval - no auth required (for board members) */}
          <Route path="/approve-payment/:token" element={<PaymentApprovalPage />} />
          {/* Public HR Update Page - no auth required */}
          <Route path="/hr-update/:token" element={<HrUpdatePage />} />
          {/* Public Email Preferences Centre - tokenised, no auth required */}
          <Route path="/email-preferences" element={<EmailPreferencesPage />} />
          {/* Public Carrier (truck vendor) registration - no auth required */}
          <Route path="/carrier-registration" element={<CarrierRegistrationPage />} />
          {/* Customer Portal - requires customer login */}
          <Route
            path="/customer-portal"
            element={
              <ProtectedRoute>
                <CustomerPortalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/carrier-approvals"
            element={
              <ProtectedRoute>
                <CarrierApprovalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-approvals"
            element={
              <ProtectedRoute>
                <CustomerApprovalsPage />
              </ProtectedRoute>
            }
          />
          {/* Partner document repository — external partner accounts */}
          <Route
            path="/partner-portal"
            element={
              <ProtectedRoute>
                <PartnerPortalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-approvals"
            element={
              <ProtectedRoute staffOnly>
                <PartnerApprovalsPage />
              </ProtectedRoute>
            }
          />
          {/* Customer Quotes - Get quote and view my quotes for customers */}
          <Route
            path="/customer-quotes"
            element={
              <ProtectedRoute>
                <CustomerQuotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-quotes-customer"
            element={
              <ProtectedRoute>
                <CustomerQuotesPage />
              </ProtectedRoute>
            }
          />
          {/* Customer Feedback & Suggestions */}
          <Route
            path="/customer-feedback"
            element={
              <ProtectedRoute>
                <CustomerFeedbackPage />
              </ProtectedRoute>
            }
          />
          {/* My Company & Contacts - Merged customer CRM for their company and contacts */}
          <Route
            path="/my-company"
            element={
              <ProtectedRoute>
                <MyCompanyContactsPage />
              </ProtectedRoute>
            }
          />
          {/* My Finance - Customer's own finance records */}
          <Route
            path="/my-finance"
            element={
              <ProtectedRoute>
                <MyFinancePage />
              </ProtectedRoute>
            }
          />
          {/* Mobile App - optimized for phone users */}
          <Route
            path="/payment-links"
            element={
              <ProtectedRoute staffOnly>
                <StaffPaymentsPage />
              </ProtectedRoute>
            }
          />
          {/* Mobile App - optimized for phone users */}
          <Route
            path="/mobile"
            element={
              <ProtectedRoute>
                <MobileAppPage />
              </ProtectedRoute>
            }
          />
          {/* Staff Mobile App - for staff on the go */}
          <Route
            path="/staff-mobile"
            element={
              <ProtectedRoute staffOnly>
                <StaffMobilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lcl"
            element={
              <ProtectedRoute>
                <LCLTrackingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/watchlist"
            element={
              <ProtectedRoute>
                <WatchlistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support-queue"
            element={
              <ProtectedRoute>
                <SupportQueuePage />
              </ProtectedRoute>
            }
          />
          {/* LinkedIn Newsletter */}
          <Route
            path="/linkedin-newsletter"
            element={
              <ProtectedRoute>
                <LinkedInNewsletterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/voice-notes"
            element={
              <ProtectedRoute>
                <VoiceNotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute>
                <APIsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metrans"
            element={<Navigate to="/tracking/metrans" replace />}
          />
          <Route
            path="/health"
            element={
              <ProtectedRoute>
                <HealthDashboardPage />
              </ProtectedRoute>
            }
          />
          {/* System Monitor - background services, emails, errors */}
          <Route
            path="/system-monitor"
            element={
              <ProtectedRoute>
                <SystemMonitorPage />
              </ProtectedRoute>
            }
          />
          {/* Partner Directory */}
          <Route
            path="/partner-directory"
            element={
              <ProtectedRoute>
                <PartnerDirectoryPage />
              </ProtectedRoute>
            }
          />
          {/* Customer Advisory - send newsletters to customers */}
          <Route
            path="/customer-advisory"
            element={
              <ProtectedRoute>
                <CustomerAdvisoryPage />
              </ProtectedRoute>
            }
          />
          {/* CRM Data Refresh — quarterly campaign UI (staff only) */}
          <Route
            path="/crm-refresh"
            element={
              <ProtectedRoute>
                <CRMDataRefreshPage />
              </ProtectedRoute>
            }
          />
          {/* CRM Data Refresh — public update form (no auth, token in URL) */}
          <Route path="/crm-refresh/update/:token" element={<CRMRefreshUpdateFormPage />} />
          {/* Offer Archive - offer@europacific.com inbox */}
          <Route
            path="/offer-archive"
            element={
              <ProtectedRoute>
                <OfferArchivePage />
              </ProtectedRoute>
            }
          />
          {/* Customer Reactivation Dashboard */}
          <Route
            path="/reactivation"
            element={
              <ProtectedRoute>
                <CustomerReactivationPage />
              </ProtectedRoute>
            }
          />
          {/* WhatsApp Support Tickets */}
          <Route
            path="/whatsapp-tickets"
            element={
              <ProtectedRoute>
                <WhatsAppTicketsPage />
              </ProtectedRoute>
            }
          />
          {/* WhatsApp Users dashboard — guest/verified, tier scoring, chat history */}
          <Route
            path="/whatsapp/users"
            element={
              <ProtectedRoute>
                <WhatsAppUsersPage />
              </ProtectedRoute>
            }
          />
          {/* Task Templates Management */}
          <Route
            path="/task-templates"
            element={
              <ProtectedRoute>
                <TaskTemplatesPage />
              </ProtectedRoute>
            }
          />
          {/* Email Offer Archive */}
          <Route
            path="/email-archive"
            element={
              <ProtectedRoute>
                <EmailArchivePage />
              </ProtectedRoute>
            }
          />
          {/* Redirect /history to /tracking/history */}
          <Route
            path="/history"
            element={<Navigate to="/tracking/history" replace />}
          />
          {/* Global Search - Default landing after login */}
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <GlobalSearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/puk-management"
            element={
              <ProtectedRoute adminOnly>
                <PukManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/import-export"
            element={
              <ProtectedRoute adminOnly>
                <ImportExportPage />
              </ProtectedRoute>
            }
          />
          {/* User Management - Admin Only */}
          <Route
            path="/user-management"
            element={
              <ProtectedRoute adminOnly>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deployment-log"
            element={
              <ProtectedRoute adminOnly>
                <DeploymentLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/weekly-reports"
            element={
              <ProtectedRoute adminOnly>
                <WeeklyReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/weekly-trends"
            element={
              <ProtectedRoute adminOnly>
                <WeeklyTrendsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-rules"
            element={
              <ProtectedRoute adminOnly>
                <EmailRulesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operations-monitor"
            element={
              <ProtectedRoute staffOnly>
                <OperationsMonitorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance"
            element={
              <ProtectedRoute staffOnly>
                <FinancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm-documents"
            element={
              <ProtectedRoute staffOnly>
                <CRMDocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm"
            element={
              <ProtectedRoute staffOnly>
                <CRMPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/campaigns"
            element={
              <ProtectedRoute staffOnly>
                <CRMCampaignsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-leads"
            element={
              <ProtectedRoute staffOnly>
                <SalesLeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/satisfaction-reviews"
            element={
              <ProtectedRoute staffOnly>
                <SatisfactionReviewsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-nps"
            element={
              <ProtectedRoute adminOnly>
                <NPSPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr-performance"
            element={
              <ProtectedRoute staffOnly>
                <HRPerformanceAutoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr-performance-manual"
            element={
              <ProtectedRoute staffOnly>
                <HRPerformancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-feedback"
            element={
              <ProtectedRoute staffOnly>
                <MyFeedbackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bl"
            element={
              <ProtectedRoute staffOnly>
                <BillOfLadingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trinet"
            element={
              <ProtectedRoute staffOnly>
                <TrinetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/port-tracking"
            element={
              <ProtectedRoute>
                <PortTrackingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/co2-calculator"
            element={
              <ProtectedRoute>
                <CO2CalculatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/co2-dashboard"
            element={
              <ProtectedRoute>
                <CO2DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-book"
            element={
              <ProtectedRoute>
                <OrderBookPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/route-calculator"
            element={
              <ProtectedRoute>
                <RouteCalculatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transport-orders"
            element={
              <ProtectedRoute>
                <TransportOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trucking-blacklist"
            element={
              <ProtectedRoute staffOnly>
                <TruckingBlacklistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sms-management"
            element={
              <ProtectedRoute>
                <SmsManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-repository"
            element={
              <ProtectedRoute>
                <EmailRepositoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/outbound-emails"
            element={
              <ProtectedRoute>
                <OutboundEmailRepositoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/position-folders"
            element={
              <ProtectedRoute>
                <PositionFoldersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/searates"
            element={
              <ProtectedRoute>
                <CmaCgmPage />
              </ProtectedRoute>
            }
          />
          <Route path="/cma-cgm" element={<Navigate to="/searates" replace />} />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <QuotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-quotes"
            element={
              <ProtectedRoute>
                <MyQuotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers"
            element={
              <ProtectedRoute staffOnly>
                <OffersDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/pricing"
            element={
              <ProtectedRoute staffOnly>
                <ErrorBoundary pageName="Pricing Module">
                  <PricingModulePage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/rate-cards"
            element={
              <ProtectedRoute staffOnly>
                <RateCardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/followups"
            element={
              <ProtectedRoute staffOnly>
                <OfferFollowupDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/campaigns"
            element={
              <ProtectedRoute staffOnly>
                <ErrorBoundary pageName="Partner Campaigns">
                  <PartnerCampaignsPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Rate Finder */}
          <Route
            path="/rate-finder"
            element={
              <ProtectedRoute staffOnly>
                <RateFinderPage />
              </ProtectedRoute>
            }
          />
          {/* Vendor AP Intelligence */}
          <Route
            path="/vendor-ap"
            element={
              <ProtectedRoute staffOnly>
                <VendorAPPage />
              </ProtectedRoute>
            }
          />
          {/* Izvrsbe (Forced Payables) */}
          <Route
            path="/izvrsbe"
            element={
              <ProtectedRoute staffOnly>
                <IzvrsbePage />
              </ProtectedRoute>
            }
          />
          {/* Bank Account Status */}
          <Route
            path="/bank-status"
            element={
              <ProtectedRoute staffOnly>
                <BankStatusPage />
              </ProtectedRoute>
            }
          />
          {/* Bank Loans Tracker */}
          <Route
            path="/loans"
            element={
              <ProtectedRoute staffOnly>
                <LoansPage />
              </ProtectedRoute>
            }
          />
          {/* AR/AP Dashboard */}
          <Route
            path="/ar-ap"
            element={
              <ProtectedRoute staffOnly>
                <ARAPPage />
              </ProtectedRoute>
            }
          />
          {/* Cash Flow Projection Dashboard */}
          <Route
            path="/cashflow-dashboard"
            element={
              <ProtectedRoute staffOnly>
                <CashFlowDashboardPage />
              </ProtectedRoute>
            }
          />
          {/* Cash Flow - Salaries & VAT */}
          <Route
            path="/cashflow"
            element={
              <ProtectedRoute staffOnly>
                <CashFlowPage />
              </ProtectedRoute>
            }
          />
          {/* Shareholder Reports */}
          <Route
            path="/shareholder-reports"
            element={
              <ProtectedRoute staffOnly>
                <ShareholderReportsPage />
              </ProtectedRoute>
            }
          />
          {/* Tracking Module routes */}
          <Route
            path="/tracking"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking/lcl"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking/air"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking/vessel"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking/port"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking/metrans"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking/watchlist"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking/history"
            element={
              <ProtectedRoute>
                <TrackingModulePage />
              </ProtectedRoute>
            }
          />
          {/* Legacy routes - redirect to new structure */}
          <Route
            path="/pricing"
            element={
              <ProtectedRoute staffOnly>
                <ErrorBoundary pageName="Pricing Module">
                  <PricingModulePage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers-dashboard"
            element={
              <ProtectedRoute staffOnly>
                <OffersDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-cards"
            element={
              <ProtectedRoute staffOnly>
                <RateCardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide"
            element={
              <ProtectedRoute>
                <UnifiedGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guides"
            element={<Navigate to="/guide" replace />}
          />
          <Route
            path="/news"
            element={
              <ProtectedRoute staffOnly>
                <NewsPage />
              </ProtectedRoute>
            }
          />
          {/* Newsletter Management */}
          <Route
            path="/newsletter"
            element={
              <ProtectedRoute staffOnly>
                <NewsletterPage />
              </ProtectedRoute>
            }
          />
          {/* Unsubscribe Dashboard — global do-not-mail list */}
          <Route
            path="/unsubscribe-dashboard"
            element={
              <ProtectedRoute staffOnly>
                <UnsubscribeDashboardPage />
              </ProtectedRoute>
            }
          />
          {/* LLM Cost Control — admin-only burn dashboard + kill switches */}
          <Route
            path="/llm-cost-control"
            element={
              <ProtectedRoute staffOnly>
                <LLMCostControlPage />
              </ProtectedRoute>
            }
          />
          {/* HRBot Dashboard */}
          <Route
            path="/hrbot"
            element={
              <ProtectedRoute staffOnly>
                <HRBotPage />
              </ProtectedRoute>
            }
          />
          {/* ComplianceBot Dashboard */}
          <Route
            path="/compliance"
            element={
              <ProtectedRoute staffOnly>
                <CompliancePage />
              </ProtectedRoute>
            }
          />
          {/* PA Operations Dashboard */}
          <Route
            path="/pa-operations"
            element={
              <ProtectedRoute staffOnly>
                <PAOperationsPage />
              </ProtectedRoute>
            }
          />
          {/* Staff Management - HR Module */}
          <Route
            path="/staff"
            element={
              <ProtectedRoute staffOnly>
                <StaffManagementPage />
              </ProtectedRoute>
            }
          />
          {/* Leave Calendar - HR Module */}
          <Route
            path="/leave-calendar"
            element={
              <ProtectedRoute staffOnly>
                <LeaveCalendarPage />
              </ProtectedRoute>
            }
          />
          {/* Customer Groups Management */}
          <Route
            path="/customer-groups"
            element={
              <ProtectedRoute staffOnly>
                <CustomerGroupsPage />
              </ProtectedRoute>
            }
          />
          {/* Contact Directory - Available to all logged in users */}
          <Route
            path="/directory"
            element={
              <ProtectedRoute>
                <ContactDirectoryPage />
              </ProtectedRoute>
            }
          />
          {/* Load Calculator - Container stuffing optimization */}
          <Route
            path="/load-calculator"
            element={
              <ProtectedRoute>
                <LoadCalculatorPage />
              </ProtectedRoute>
            }
          />
          {/* Public driver tracking page - no auth required */}
          <Route path="/track/:orderId/:token" element={<DriverTrackingPage />} />
          {/* WMS - myCargoCFS */}
          <Route path="/wms" element={<WMSLandingPage />} />
          <Route path="/wms/app" element={<WMSPage />} />
          <Route path="/wms/customer" element={<WMSCustomerPortalPage />} />
          <Route path="/mycargocfs" element={<WMSLandingPage />} />
          {/* Utilities */}
          <Route path="/utilities" element={<UtilitiesPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* Global widgets - only show on internal app pages */}
        <GlobalWidgets />
        {/* Broadcast notifications */}
        <BroadcastNotifications />
        {/* Admin-only: pending registration approvals (pulses red when ≥1) */}
        <PendingApprovalsBadge />
        {/* Support request notifications for staff */}
        <SupportRequestNotifications />
        {/* Task assignment popup notifications */}
        <TaskAssignmentNotifications />
        {/* Global email sync status indicator */}
        <EmailSyncStatus />
        {/* Global tasks panel at bottom - show for staff on internal pages */}
        <StaffTasksPanel />
        <Toaster position="top-right" richColors />
        </MobileViewWrapper>
        </BrowserRouter>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
